// @greenlight-spec: 28-it-dashboard-ui.md, 19-design-system.md, 57-screenshot-walkthroughs.md
import * as React from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { Walkthrough as WalkthroughManifest } from '@/walkthroughs/schema';

export interface WalkthroughProps {
  readonly walkthrough: WalkthroughManifest;
  /** Fills `{name}` tokens in the instructions and prerequisites from the page's own data. */
  readonly values?: Readonly<Record<string, string>>;
  readonly className?: string;
}

const PLACEHOLDER = /\{([a-zA-Z][a-zA-Z0-9]*)\}/g;

export function fillPlaceholders(text: string, values: Readonly<Record<string, string>>): string {
  return text.replace(PLACEHOLDER, (token, name: string) => values[name] ?? token);
}

function imageUrl(walkthroughId: string, file: string): string {
  return `/walkthroughs/${walkthroughId}/${file}`;
}

/**
 * Numbered steps a person follows on a surface Greenlight does not control: a
 * vendor console, a desktop app, the installer, a CLI. Each step is an
 * instruction with an optional screenshot; the thumbnail opens full size with
 * previous/next across the steps that have one. Light-theme captures sit in a
 * neutral frame so they read in both themes.
 */
export function Walkthrough({
  walkthrough,
  values = {},
  className,
}: WalkthroughProps): React.ReactElement {
  const [openStep, setOpenStep] = React.useState<number | null>(null);
  const steps = walkthrough.steps.map((step) => ({
    ...step,
    instruction: fillPlaceholders(step.instruction, values),
  }));
  const illustrated = steps.flatMap((step, index) => (step.image ? [index] : []));

  const moveTo = (direction: -1 | 1): void => {
    if (openStep === null) return;
    const at = illustrated.indexOf(openStep);
    const next = illustrated[at + direction];
    if (next !== undefined) setOpenStep(next);
  };

  const open = openStep === null ? null : steps[openStep];
  const openAt = openStep === null ? -1 : illustrated.indexOf(openStep);

  return (
    <div className={cn('space-y-4 text-sm', className)}>
      {walkthrough.prerequisites.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
          {walkthrough.prerequisites.map((prerequisite) => (
            <li key={prerequisite}>{fillPlaceholders(prerequisite, values)}</li>
          ))}
        </ul>
      )}

      {walkthrough.startUrl !== undefined && (
        <a
          href={walkthrough.startUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center gap-1.5 font-medium text-foreground underline-offset-4 hover:underline md:min-h-0"
        >
          Open {walkthrough.vendor}
          <ExternalLink aria-hidden="true" className="size-3.5" />
        </a>
      )}

      <ol className="space-y-4">
        {steps.map((step, index) => (
          <li key={step.id} className="flex gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-medium text-foreground"
            >
              {index + 1}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
              <p className="min-w-0 flex-1 leading-relaxed text-foreground">
                <span className="sr-only">Step {index + 1}. </span>
                {step.instruction}
              </p>
              {step.image !== undefined && (
                <button
                  type="button"
                  onClick={() => setOpenStep(index)}
                  aria-label={`Enlarge the screenshot for step ${index + 1}`}
                  className="w-full shrink-0 overflow-hidden rounded-gl border border-border bg-muted p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-44"
                >
                  <img
                    src={imageUrl(walkthrough.id, step.image)}
                    alt={step.alt ?? ''}
                    loading="lazy"
                    className="aspect-[16/10] w-full rounded-[calc(var(--radius)-2px)] object-contain"
                  />
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>

      <p className="text-xs text-muted-foreground">
        Verified against {walkthrough.vendor} on{' '}
        <time dateTime={walkthrough.verified.on} className="whitespace-nowrap">
          {walkthrough.verified.on}
        </time>
      </p>

      <Dialog open={open !== null} onOpenChange={(isOpen) => !isOpen && setOpenStep(null)}>
        <DialogContent className="max-w-[min(96vw,1200px)] gap-3 p-4">
          {open?.image !== undefined && (
            <>
              <DialogTitle className="pr-8 text-sm font-medium">
                Step {openStep! + 1} of {steps.length}
              </DialogTitle>
              <div className="overflow-hidden rounded-gl border border-border bg-muted p-1">
                <img
                  src={imageUrl(walkthrough.id, open.image)}
                  alt={open.alt ?? ''}
                  className="max-h-[70vh] w-full object-contain"
                />
              </div>
              <DialogDescription className="text-foreground">{open.instruction}</DialogDescription>
              <div className="flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => moveTo(-1)}
                  disabled={openAt <= 0}
                >
                  <ChevronLeft aria-hidden="true" className="size-4" />
                  Previous screenshot
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => moveTo(1)}
                  disabled={openAt >= illustrated.length - 1}
                >
                  Next screenshot
                  <ChevronRight aria-hidden="true" className="size-4" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
